import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Icon,
  IconButton,
  IconButtonProps,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SxProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Apps as AppsIcon,
  Login as LoginIcon,
  AppRegistration as AppRegistrationIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import DiamondIcon from '@mui/icons-material/Diamond';

import { getAuthState } from '../../store/AuthSlice';
import { getAccessLimitState } from '../../store/AccessLimitSlice';
import {
  INavigationRoutesV2,
  APP_NAVIGATION_OPTIONS,
  NAVIGATION_OPTION_KEYS,
} from '../../navigation';
import { TP_COLORS } from '../../constants';
import { useAppSelector } from '../../hooks';
import { checkUserPermission } from '../../helper';
import { getFlexiblePlansState } from '../../store/V2/FlexiblePlansSlice';
import { getSubscriptionPlansState } from '../../store/V2/SubscriptionPlansSlice';

interface NavigationOptionsIconButtonProps {
  iconButtonProps?: IconButtonProps;
  iconStyles?: SxProps;
}

const NavigationOptionsIconButton: React.FC<NavigationOptionsIconButtonProps> = ({
  iconButtonProps = {},
  iconStyles = {
    color: '#01A79F',
  },
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  // Store
  const authState = useAppSelector(getAuthState);
  const { lpAccess } = useAppSelector(getAccessLimitState);
  const subscriptionPlansState = useAppSelector(getSubscriptionPlansState);
  const flexiblePlansState = useAppSelector(getFlexiblePlansState);

  // State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const authUser = authState.user;
  const open = Boolean(anchorEl);
  const isSignInSignUpVisible =
    !authUser || (authUser && authUser.role.isGuest);
  const isUpgradeVisible =
    isSignInSignUpVisible || (authUser && authUser.role.name === 'Student');

  const classBookingUrl = `${process.env.REACT_APP_TUTOPIYA_PLATFORM_BASE_URL}/#/dashboard/consultee/consultant`;
  const isTrialRecordingsInUrl = window.location.href.includes(
    'trial-recording'
  );

  const redirectURL = location.pathname.replace('/learning-portal', '').trim();
  const flexiblePlanList = flexiblePlansState.userFlexiblePlans || [];
  const subscriptionPlanList =
    subscriptionPlansState.userSubscriptionPlans || [];

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const filterNavigationOptions = (
    userNavigationRoute: INavigationRoutesV2
  ) => {
    let hasPermission = true;

    switch (userNavigationRoute.key) {
      case NAVIGATION_OPTION_KEYS.QUIZ_HISTORY:
        hasPermission = checkUserPermission(authUser, {
          quizHistory: ['view'],
        });
        break;

      case NAVIGATION_OPTION_KEYS.LESSON_HISTORY:
        hasPermission =
          checkUserPermission(authUser, {
            adminLessonHistory: ['view'],
          }) ||
          checkUserPermission(authUser, {
            lessonHistory: ['view'],
          });
        break;

      case NAVIGATION_OPTION_KEYS.SCHEDULE:
        hasPermission =
          checkUserPermission(authUser, {
            schedule: ['view'],
          }) ||
          checkUserPermission(authUser, {
            adminSchedule: ['view'],
          });
        break;

      case NAVIGATION_OPTION_KEYS.RESCHEDULE_REQUESTS:
        hasPermission = checkUserPermission(authUser, {
          adminSchedule: ['view'],
        });
        break;

      case NAVIGATION_OPTION_KEYS.SUBSCRIPTIONS:
        hasPermission = lpAccess;
        break;

      case NAVIGATION_OPTION_KEYS.TRIAL_CLASSES:
        hasPermission =
          checkUserPermission(authState.user, {
            trialClass: ['view'],
          }) ||
          checkUserPermission(authState.user, {
            adminTrialClass: ['view'],
          });
        break;

      case NAVIGATION_OPTION_KEYS.USERS:
        hasPermission = checkUserPermission(authState.user, {
          adminUsers: ['view'],
        });
        break;

      case NAVIGATION_OPTION_KEYS.STUDENTS:
        hasPermission = checkUserPermission(authUser, {
          adminStudent: ['view'],
        });
        break;

      case NAVIGATION_OPTION_KEYS.BILLING:
        hasPermission =
          checkUserPermission(authUser, {
            billing: ['view'],
          }) &&
          (flexiblePlanList.length > 0 || subscriptionPlanList.length > 0);
        break;

      case NAVIGATION_OPTION_KEYS.QUESTIONS:
        hasPermission = checkUserPermission(authUser, {
          adminQuestion: ['view'],
        });
        break;

      case NAVIGATION_OPTION_KEYS.QUESTION_BANK:
        hasPermission = checkUserPermission(authUser, {
          adminQuestion: ['view'],
        });
        break;

      case NAVIGATION_OPTION_KEYS.QUIZ_BANK:
        hasPermission = checkUserPermission(authUser, {
          adminQuiz: ['view'],
        });
        break;

      default:
        hasPermission = true;
        break;
    }

    return hasPermission;
  };

  const handleNavigationChange = (userNavigationRoute: INavigationRoutesV2) => {
    setTimeout(() => {
      navigate(userNavigationRoute.pathname);
    }, 250);

    closeMenu();
  };

  const menuOptions = [...APP_NAVIGATION_OPTIONS].filter(
    filterNavigationOptions
  );

  return (
    <>
      <IconButton
        id="nav-bar-menu-button"
        aria-controls={open ? 'nav-bar-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        size="large"
        edge="start"
        color="primary"
        aria-label="menu"
        sx={{ mr: 2, ...iconStyles }}
        onClick={openMenu}
        {...iconButtonProps}
      >
        <AppsIcon fontSize="large" />
      </IconButton>
      <Menu
        id="nav-bar-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        PaperProps={{ style: { borderRadius: '12px', minWidth: '300px' } }}
        MenuListProps={{
          'aria-labelledby': 'nav-bar-menu-button',
          style: { padding: '15px' },
        }}
      >
        {isUpgradeVisible && belowMd && (
          <MenuItem
            onClick={() =>
              navigate(`${process.env.PUBLIC_URL}/dashboard/upgrade`)
            }
            style={{
              padding: '15px',
              borderRadius: '7px',
              backgroundColor: TP_COLORS.yellow.light,
            }}
          >
            <ListItemIcon>
              <DiamondIcon />
            </ListItemIcon>
            <ListItemText primary={lpAccess ? 'Upgraded' : 'Upgrade'} />
          </MenuItem>
        )}
        {menuOptions.map((menuOption, index) => {
          // let isActive = false;

          // if (menuOption.matches && menuOption.matches.length > 0) {
          //   menuOption.matches.forEach((match) => {
          //     if (matchPath(match, location.pathname)) {
          //       isActive = true;
          //     }
          //   });
          // }

          return (
            <MenuItem
              key={index}
              // selected={isActive}
              onClick={() => handleNavigationChange(menuOption)}
              style={{ padding: '15px', borderRadius: '7px' }}
            >
              <ListItemIcon>
                <Icon
                  color="inherit"
                  // color={isActive ? 'primary' : 'inherit'}
                  component={menuOption.icon}
                />
              </ListItemIcon>
              <ListItemText
                primary={menuOption.name}
                primaryTypographyProps={{
                  color: 'secondary',
                  // color: isActive ? 'primary' : 'secondary',
                }}
              />
            </MenuItem>
          );
        })}

        {isSignInSignUpVisible ? (
          <div>
            {belowMd ? (
              <MenuItem
                onClick={() =>
                  navigate(
                    `${process.env.PUBLIC_URL}/login?${
                      redirectURL ? `redirect=${redirectURL}` : ''
                    }`
                  )
                }
                style={{ padding: '15px', borderRadius: '7px' }}
              >
                <ListItemIcon>
                  <Icon color="inherit" component={LoginIcon} />
                </ListItemIcon>
                <ListItemText
                  primary="Sign In"
                  primaryTypographyProps={{
                    color: 'secondary',
                  }}
                />
              </MenuItem>
            ) : null}
            {!isTrialRecordingsInUrl && belowMd ? (
              <MenuItem
                onClick={() =>
                  navigate(
                    `${process.env.PUBLIC_URL}/sign-up?${
                      redirectURL ? `redirect=${redirectURL}` : ''
                    }`
                  )
                }
                style={{ padding: '15px', borderRadius: '7px' }}
              >
                <ListItemIcon>
                  <Icon color="inherit" component={AppRegistrationIcon} />
                </ListItemIcon>
                <ListItemText
                  primary="Sign Up"
                  primaryTypographyProps={{
                    color: 'secondary',
                  }}
                />
              </MenuItem>
            ) : null}
            {isTrialRecordingsInUrl ? (
              <MenuItem
                component="a"
                href={classBookingUrl}
                target="_blank"
                onClick={closeMenu}
                style={{ padding: '15px', borderRadius: '7px' }}
              >
                <ListItemIcon>
                  <Icon color="inherit" component={CalendarTodayIcon} />
                </ListItemIcon>
                <ListItemText
                  primary="Book Classes With Tutopiya"
                  primaryTypographyProps={{
                    color: 'secondary',
                  }}
                />
              </MenuItem>
            ) : null}
          </div>
        ) : null}
      </Menu>
    </>
  );
};

export default NavigationOptionsIconButton;
